import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Volume2, Square, Loader2, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VoicePlayer({ text, userId, source = 'general', label = null, variant = 'ghost', size = 'icon', className = '' }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [savedUrl, setSavedUrl] = useState(null);
  const audioRef = useRef(null);
  const cachedB64 = useRef(null);
  const cachedText = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  };

  const playAudio = (dataUrl) => {
    const audio = new Audio(dataUrl);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => { setPlaying(false); toast.error('Audio playback failed'); };
    audio.play();
    setPlaying(true);
  };

  const handleClick = async () => {
    if (playing) return stopAudio();
    if (cachedB64.current && cachedText.current === text) {
      return playAudio(cachedB64.current);
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/voice/tts`, {
        text,
        user_id: userId,
        source_type: source
      });
      cachedB64.current = res.data.audio_b64;
      cachedText.current = text;
      setSavedUrl(`${BACKEND_URL}${res.data.audio_url}`);
      playAudio(res.data.audio_b64);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Voice generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={loading || !text}
        title={playing ? 'Stop' : 'Listen'}
        data-testid="voice-play-btn"
        className={size === 'icon' ? 'h-7 w-7' : ''}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : playing ? (
          <Square className="h-4 w-4" />
        ) : (
          <Volume2 className="h-4 w-4" />
        )}
        {label && <span className="ml-2">{loading ? 'Generating...' : playing ? 'Stop' : label}</span>}
      </Button>
      {savedUrl && (
        <a
          href={savedUrl}
          download
          title="Download saved audio"
          data-testid="voice-download-link"
          className="text-muted-foreground hover:text-primary"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
      )}
    </span>
  );
}
